import React, { useEffect, useState, ChangeEvent } from "react";
import {
  useFireDrills,
  useOrgUsersStore,
  usePremisesStore,
  useRegionStore,
} from "../../store/store";
import "react-datepicker/dist/react-datepicker.css";
import "../../App.css";
import { useS3Upload, deleteS3File } from "../../utils/s3Client";
import SendFireDrillEmail from "./SendFireDrillEmail";
import type { FireDrill } from "../../types/api";
import { CreateDrillSection } from "./CreateDrillSection";
import { PerformDrillSection } from "./PerformDrillSection";

type FormMode = "create" | "perform";

interface OrgUser {
  _id: { $oid: string };
  firstName: string;
  lastName: string;
  employeeId: string;
  email: string;
  mobile: string;
  premise?: string | Array<{ $oid: string }> | string[];
}

interface FireDrillFormData {
  _id?: string;
  region: string;
  premise: string;
  premisesId: string;
  report_send_email?: string[];
  date_of_test_drills: string | Date | null;
  person_responsibility: string;
  closing_timeline: Date | string | null;
  date_of_evidence: string | Date | null;
  participation_rate: string | number;
  observation_identified_for_improvement: string;
  area: string;
  floor: string;
  users?: string[];
  actions: string;
  closure_evidence: string;
  average_drill: string | number;
  evacuation_trend: string | number;
  next_drill_scheduled: string | Date | null;
  evidence_closure_files: File[] | string[] | null;
  status?: "scheduled" | "completed";
  moduleType?: string;
  feedback_gap_analysis?: string;
}

interface Premise {
  id: string;
  premisesName: string;
  responsiblePerson: string;
  region: string;
  floor: string;
  [key: string]: any;
}

interface Region {
  _id: string;
  regionName?: string;
  [key: string]: any;
}

interface CreateFireDrillProps {
  onClose: () => void;
  fireDrillToEdit: FireDrillFormData | null;
  isEdit: boolean;
  initialMode?: "create" | "perform";
  moduleName?: string;
}

const CreateFireDrill: React.FC<CreateFireDrillProps> = ({
  onClose,
  fireDrillToEdit,
  isEdit,
  initialMode = "create",
  moduleName = "fire_safety",
}) => {
  const [premisesList, setPremisesList] = useState<Premise[]>([]);
  const [mode, setMode] = useState<FormMode>(initialMode);
  const [regionList, setRegionList] = useState<string[]>([]);
  const [emails, setEmails] = useState<string[]>([""]);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string>("");
  const [emailErrorMessage, setEmailErrorMessage] = useState<string>("");
  const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [listOfUsersFromPremisesId, setListOfUsersFromPremisesId] = useState<
    OrgUser[]
  >([]);

  const {
    uploadFileToS3,
    progress: uploadProgress,
    uploading,
    error: uploadError,
  } = useS3Upload();

  const [formData, setFormData] = useState<FireDrillFormData>({
    region: "",
    premise: "",
    premisesId: "",
    date_of_test_drills: null,
    person_responsibility: "",
    closing_timeline: null,
    date_of_evidence: null,
    participation_rate: "",
    area: "",
    floor: "",
    users: [],
    observation_identified_for_improvement: "",
    actions: "",
    closure_evidence: "",
    average_drill: "",
    evacuation_trend: "",
    next_drill_scheduled: null,
    evidence_closure_files: null,
    status: "scheduled",
    moduleType: moduleName,
    feedback_gap_analysis: "",
  });

  const { allPremises } = usePremisesStore();
  const { createFireDrill, updateFireDrill } = useFireDrills();
  const { regions } = useRegionStore();
  const { fetchOrgUsers, orgUsersData } = useOrgUsersStore();

  useEffect(() => {
    fetchOrgUsers();
  }, []);

  const { sendEmail, emailErrorMessage: sendEmailError } = SendFireDrillEmail({
    fireDrillData: formData as FireDrill,
    recipientEmails: emails.filter((email) => email.trim() !== ""),
    onError: (error: string) => setEmailErrorMessage(error),
  });

  const getRegionIdByName = (regionName: string): string => {
    const region = regions.find((r) => r.regionName?.trim() === regionName);
    return region?._id || "";
  };

  const handleUserSelectChange = (userIds: string[]) => {
    setFormData((prev) => ({
      ...prev,
      users: userIds,
    }));
  };

  const filterUserByPremisesId = (premisesId: string) => {
    if (!premisesId || !orgUsersData) {
      setListOfUsersFromPremisesId([]);
      return;
    }

    const usersForPremise = orgUsersData.filter((user: any) => {
      if (typeof user.premise === "string") {
        return user.premise === premisesId;
      }

      if (
        Array.isArray(user.premise) &&
        user.premise.length > 0 &&
        typeof user.premise[0] === "string"
      ) {
        return user.premise.includes(premisesId);
      }

      if (
        Array.isArray(user.premise) &&
        user.premise.length > 0 &&
        typeof user.premise[0] === "object"
      ) {
        return user.premise.some((_p: any) => true);
      }

      return false;
    });

    setListOfUsersFromPremisesId(usersForPremise);

    if (isEdit && fireDrillToEdit?.users) {
      setFormData((prev) => ({
        ...prev,
        users: fireDrillToEdit.users || [],
      }));
    }
  };

  useEffect(() => {
    if (allPremises.length > 0) {
      if (formData.region) {
        const regionId = getRegionIdByName(formData.region);
        const filteredFacilities = allPremises
          .filter((premise: any) => premise.region === regionId)
          .map((premise: any) => ({
            id: premise.id,
            premisesName: premise.premisesName.trim(),
            responsiblePerson: premise.responsiblePerson,
            floor: premise.floor || "",
            region: premise.region,
          }));

        setPremisesList(filteredFacilities);
      } else {
        const uniqueFacilities = allPremises.map((premise: any) => ({
          id: premise.id,
          premisesName: premise.premisesName.trim(),
          responsiblePerson: premise.responsiblePerson,
          floor: premise.floor || "",
          region: premise.region,
        }));
        setPremisesList(uniqueFacilities);
      }
    }
  }, [formData.region, allPremises]);

  useEffect(() => {
    if (allPremises.length > 0) {
      const uniqueRegionList = Array.from(
        new Set(regions.map((region) => region.regionName || ""))
      );
      setRegionList(uniqueRegionList.filter(Boolean));
    }

    if (isEdit && fireDrillToEdit) {
      const parseDate = (dateString: string | undefined): Date | null => {
        if (!dateString) return null;
        const [day, month, year] = dateString.split("/");
        return new Date(`${month}/${day}/${year}`);
      };

      const parseTime = (timeString: string | undefined): Date | null => {
        if (!timeString) return null;
        const [hours, minutes] = timeString.split(":");
        const date = new Date();
        date.setHours(parseInt(hours, 10));
        date.setMinutes(parseInt(minutes, 10));
        return date;
      };

      setFormData({
        region: fireDrillToEdit.region || "",
        premise: fireDrillToEdit.premise || "",
        premisesId: fireDrillToEdit.premisesId || "",
        person_responsibility: fireDrillToEdit.person_responsibility || "",
        date_of_test_drills: parseDate(
          typeof fireDrillToEdit.date_of_test_drills === "string"
            ? fireDrillToEdit.date_of_test_drills
            : undefined
        ),
        date_of_evidence: parseDate(
          typeof fireDrillToEdit.date_of_evidence === "string"
            ? fireDrillToEdit.date_of_evidence
            : undefined
        ),
        next_drill_scheduled: parseDate(
          typeof fireDrillToEdit.next_drill_scheduled === "string"
            ? fireDrillToEdit.next_drill_scheduled
            : undefined
        ),
        closing_timeline: parseTime(
          typeof fireDrillToEdit.closing_timeline === "string"
            ? fireDrillToEdit.closing_timeline
            : undefined
        ),
        area: fireDrillToEdit.area || "",
        floor: fireDrillToEdit.floor || "",
        participation_rate: fireDrillToEdit.participation_rate || "",
        observation_identified_for_improvement:
          fireDrillToEdit.observation_identified_for_improvement || "",
        actions: fireDrillToEdit.actions || "",
        closure_evidence: fireDrillToEdit.closure_evidence || "",
        evidence_closure_files: fireDrillToEdit.evidence_closure_files || null,
        average_drill: fireDrillToEdit.average_drill || "",
        evacuation_trend: fireDrillToEdit.evacuation_trend || "",
        users: fireDrillToEdit.users || [],
        moduleType: fireDrillToEdit.moduleType || moduleName,
        feedback_gap_analysis: fireDrillToEdit.feedback_gap_analysis || "",
      });

      if (fireDrillToEdit.premisesId) {
        filterUserByPremisesId(fireDrillToEdit.premisesId);
      }

      if ((fireDrillToEdit.report_send_email ?? []).length > 0) {
        setEmails(fireDrillToEdit.report_send_email!);
      }
    }
  }, [isEdit, fireDrillToEdit, regions, allPremises, moduleName]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Common validations
    if (!formData.region) newErrors.region = "Region is required";
    if (!formData.premise) newErrors.premise = "Premise is required";
    if (!formData.premisesId) newErrors.premise = "Premise is required";
    if (!formData.date_of_test_drills)
      newErrors.date_of_test_drills = "Date of test/drill is required";
    if (!formData.person_responsibility)
      newErrors.person_responsibility = "Responsible person is required";

    if (mode === "perform") {
      // Perform drill specific validations
      if (!formData.closing_timeline)
        newErrors.closing_timeline = "Timeline to close is required";
      if (!formData.date_of_evidence)
        newErrors.date_of_evidence = "Date of evidence is required";
      if (!formData.observation_identified_for_improvement)
        newErrors.observation_identified_for_improvement =
          "Observation is required";
      if (!formData.actions) newErrors.actions = "Action is required";
      if (!formData.closure_evidence)
        newErrors.closure_evidence = "Evidence of closure is required";
      if (!formData.average_drill)
        newErrors.average_drill = "Average drill duration is required";
      if (!formData.evacuation_trend)
        newErrors.evacuation_trend = "Evacuation time trend is required";
      if (!formData.next_drill_scheduled)
        newErrors.next_drill_scheduled = "Next drill scheduled is required";
      if (moduleName === "external_emergency_preparedness" && !formData.feedback_gap_analysis)
        newErrors.feedback_gap_analysis = "Feedback & Gap Analysis is required";
    }

    // Date validations
    if (
      formData.date_of_test_drills &&
      formData.date_of_evidence &&
      formData.date_of_test_drills > formData.date_of_evidence
    ) {
      newErrors.date_of_evidence =
        "Date of evidence cannot be before test date";
    }

    // Number validations
    if (formData.average_drill && isNaN(Number(formData.average_drill))) {
      newErrors.average_drill = "Average drill duration must be a number";
    }
    if (formData.evacuation_trend && isNaN(Number(formData.evacuation_trend))) {
      newErrors.evacuation_trend = "Evacuation time trend must be a number";
    }

    // Email validations
    emails.forEach((email, index) => {
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        newErrors[`email_${index}`] = "Invalid email format";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, files } = e.target as HTMLInputElement;

    if (name === "region") {
      setFormData((prev) => ({
        ...prev,
        region: value,
        premise: "",
        premisesId: "",
        person_responsibility: "",
        floor: "",
      }));
    } else if (name === "premise") {
      const selectedPremise = premisesList.find(
        (premise) => premise.id === value
      );
      setFormData((prev) => ({
        ...prev,
        premise: selectedPremise?.premisesName || "",
        premisesId: value,
        person_responsibility: selectedPremise?.responsiblePerson || "",
        floor: selectedPremise?.floor || "",
      }));

      filterUserByPremisesId(value);
    } else if (name === "person_responsibility") {
      setFormData((prev) => ({
        ...prev,
        person_responsibility: value,
      }));
    } else if (name === "evidence_closure_files" && files) {
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      const validFiles = Array.from(files).filter(
        (file) => file.size <= maxSize
      );
      if (validFiles.length !== files.length) {
        setUploadErrorMessage("Some files exceed the 5MB size limit.");
      }
      setFormData((prev) => ({
        ...prev,
        evidence_closure_files: validFiles.length > 0 ? validFiles : null,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear error when field is changed
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const getFileKey = (value: string): string =>
    value.includes("amazonaws.com/")
      ? decodeURIComponent(value.split("amazonaws.com/")[1])
      : value;

  const handleRemoveFile = (index: number) => {
    const removed = Array.isArray(formData.evidence_closure_files)
      ? formData.evidence_closure_files[index]
      : undefined;

    setFormData((prev) => {
      let updatedFiles: File[] | string[] = [];
      if (Array.isArray(prev.evidence_closure_files)) {
        if (
          (prev.evidence_closure_files as Array<string | File>).every(
            (f) => typeof f === "string"
          )
        ) {
          updatedFiles = [...(prev.evidence_closure_files as string[])];
        } else {
          updatedFiles = [...(prev.evidence_closure_files as File[])];
        }
      }
      updatedFiles.splice(index, 1);
      return {
        ...prev,
        evidence_closure_files: updatedFiles.length > 0 ? updatedFiles : null,
      };
    });

    if (typeof removed === "string") {
      deleteS3File(getFileKey(removed)).catch(() => {});
    }
  };

  const handleAddEmail = () => {
    setEmails([...emails, ""]);
  };

  const handleRemoveEmail = (index: number) => {
    if (emails.length <= 1) return;
    const newEmails = [...emails];
    newEmails.splice(index, 1);
    setEmails(newEmails);

    if (errors[`email_${index}`]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`email_${index}`];
        return newErrors;
      });
    }
  };

  const handleEmailChange = (index: number, value: string) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);

    if (errors[`email_${index}`]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`email_${index}`];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploadErrorMessage("");
    setEmailErrorMessage("");

    if (!validateForm()) {
      return;
    }

    let participationRate: string | number = formData.participation_rate;
    if (mode === "create") {
      const totalUsers = Math.max(0, listOfUsersFromPremisesId.length - 1);
      const attendingUsers = formData.users?.length || 0;
      participationRate =
        totalUsers > 0 ? Math.round((attendingUsers / totalUsers) * 100) : 0;
    }

    // Format dates and prepare data
    const formatDate = (date: Date | null): string => {
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "";
      return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
    };

    const formatTime = (date: Date | null): string => {
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "";
      return `${date.getHours()}:${date.getMinutes()}`;
    };

    let s3FileUrls: string[] = [];
    try {
      if (
        formData.evidence_closure_files &&
        formData.evidence_closure_files?.length > 0
      ) {
        for (const file of formData.evidence_closure_files) {
          if (typeof file === "string") {
            s3FileUrls.push(file);
            continue;
          }
          const s3Url = await uploadFileToS3(file, {
            premiseId: formData.premisesId,
          });
          if (s3Url) s3FileUrls.push(s3Url);
        }
      }
    } catch (error) {
      setUploadErrorMessage(
        `File upload failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
      return;
    }

    const toDate = (value: string | Date | null): Date | null => {
      if (!value) return null;
      if (value instanceof Date) return value;
      const parts = value.split("/");
      if (parts.length === 3) {
        const [day, month, year] = parts;
        const parsed = new Date(`${month}/${day}/${year}`);
        if (!isNaN(parsed.getTime())) return parsed;
      }
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    };

    const formDataWithEmails = {
      ...formData,
      moduleType: moduleName, // Add module type
      status: mode === "perform" ? "completed" : "scheduled",
      premisesId: formData.premisesId,
      participation_rate: participationRate.toString(),
      date_of_test_drills: formatDate(toDate(formData.date_of_test_drills)),
      date_of_evidence: formatDate(toDate(formData.date_of_evidence)),
      next_drill_scheduled: formatDate(toDate(formData.next_drill_scheduled)),
      closing_timeline: formatTime(
        formData.closing_timeline instanceof Date
          ? formData.closing_timeline
          : toDate(formData.closing_timeline as string)
      ),
      area: formData.area || "",
      floor: formData.floor || "",
      average_drill: String(formData.average_drill || ""),
      evacuation_trend: String(formData.evacuation_trend || ""),
      report_send_email: emails.filter((email) => email.trim() !== ""),
      evidence_closure_files: s3FileUrls,
    };

    try {
      setIsSendingEmail(true);
      if (isEdit && fireDrillToEdit?._id) {
        await updateFireDrill(formDataWithEmails, fireDrillToEdit._id);
      } else {
        await createFireDrill(formDataWithEmails);
      }

      if (formDataWithEmails.report_send_email.length > 0) {
        await sendEmail();
      }

      onClose();
    } catch (error) {
      setUploadErrorMessage(
        `Submission failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      setIsSendingEmail(false);
    }
  };

  const getTitle = () => {
    const isHazardous = moduleName === "hazardous_materials";
    const drillType = isHazardous ? "Hazardous Materials Drill" : "Fire Drill";

    if (isEdit) {
      return `Edit ${drillType} - ${
        mode === "create" ? "Schedule" : "Performance"
      }`;
    }
    return mode === "create" ? `Create ${drillType}` : `Perform ${drillType}`;
  };

  return (
    <>
      {(uploading || isSendingEmail) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full text-center">
            <div className="flex justify-center mb-4">
              <svg
                className="animate-spin h-8 w-8 text-teal-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
            <h3 className="text-lg font-medium mb-2">
              {uploading ? "Uploading files..." : "Sending email..."}
            </h3>
            {uploading && (
              <p className="text-sm text-gray-600">
                Progress: {uploadProgress}%
              </p>
            )}
          </div>
        </div>
      )}
      <div className="flex flex-col h-[90vh]">
        <div className="flex-none px-6 pt-6">
          <h2 className="text-2xl font-semibold">{getTitle()}</h2>
          <div className="flex space-x-4 mt-2">
            <button
              type="button"
              className={`px-4 py-1 rounded-t-lg ${
                mode === "create" ? "bg-teal-600 text-white" : "bg-gray-200"
              } ${mode === "perform" ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={() => mode !== "perform" && setMode("create")}
              disabled={mode === "perform"}
            >
              {moduleName === "hazardous_materials"
                ? "Create Hazardous Drill"
                : "Create Drill"}
            </button>
            <button
              type="button"
              className={`px-4 py-1 rounded-t-lg ${
                mode === "perform" ? "bg-teal-600 text-white" : "bg-gray-200"
              } ${
                isEdit && formData.status === "completed"
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
              onClick={
                isEdit && formData.status === "completed"
                  ? undefined
                  : () => setMode("perform")
              }
              disabled={
                !formData.premisesId ||
                (isEdit && formData.status === "completed") ||
                mode === "create"
              }
            >
              {moduleName === "hazardous_materials"
                ? "Perform Hazardous Drill"
                : "Perform Drill"}
            </button>
          </div>
          <hr className="mt-2 border-2 border-gray-400" />
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {uploadErrorMessage && (
            <div className="mb-4 p-2 bg-red-100 text-red-700 rounded">
              {uploadErrorMessage}
            </div>
          )}
          {emailErrorMessage && (
            <div className="mb-4 p-2 bg-red-100 text-red-700 rounded">
              {emailErrorMessage}
            </div>
          )}
          <form
            onSubmit={
              formData.status === "completed"
                ? (e) => e.preventDefault()
                : handleSubmit
            }
            id="fireDrillForm"
          >
            {mode === "create" ? (
              <CreateDrillSection
                formData={formData}
                errors={errors}
                handleChange={handleChange}
                premisesList={premisesList}
                regionList={regionList}
                listOfUsersFromPremisesId={listOfUsersFromPremisesId}
                handleUserSelectChange={handleUserSelectChange}
                setFormData={setFormData}
                moduleName={moduleName}
              />
            ) : (
              <PerformDrillSection
                formData={formData}
                errors={errors}
                handleChange={handleChange}
                handleRemoveFile={handleRemoveFile}
                uploading={uploading}
                uploadProgress={uploadProgress}
                emails={emails}
                handleEmailChange={handleEmailChange}
                listOfUsersFromPremisesId={listOfUsersFromPremisesId}
                handleAddEmail={handleAddEmail}
                handleRemoveEmail={handleRemoveEmail}
                setFormData={setFormData}
                moduleName={moduleName}
              />
            )}
          </form>
        </div>
        <div className="flex-none py-3 px-6 bg-gray-600 flex justify-end gap-4 rounded-bl-xl rounded-br-xl">
          <button
            type="button"
            className="bg-gray-300 text-gray-800 px-6 py-2 rounded-full"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="fireDrillForm"
            className="bg-teal-600 text-white px-6 py-2 rounded-full hover:bg-teal-700 flex items-center justify-center gap-2"
            disabled={uploading || isSendingEmail}
          >
            {isSendingEmail ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {isEdit ? "Updating..." : "Submitting..."}
              </>
            ) : isEdit ? (
              "Update"
            ) : (
              "Submit"
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default CreateFireDrill;
